import React, { useEffect, useState, useRef } from 'react';
import { Note, NoteColor, NoteFormData } from '../types';
import { COLOR_OPTIONS, NOTE_COLORS } from '../constants';
import { XIcon, StarIcon, TrashIcon, MagicIcon } from './Icons';
import { refineText } from '../services/ai';

interface EditorProps {
  isOpen: boolean;
  initialData: Note | null;
  onClose: () => void;
  onSave: (data: NoteFormData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const Editor: React.FC<EditorProps> = ({ isOpen, initialData, onClose, onSave, onDelete }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [color, setColor] = useState<NoteColor>('white');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  
  // AI State
  const [isRefining, setIsRefining] = useState(false);
  
  // Refs for logic control
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const lastLoadedIdRef = useRef<string | null>(null);

  // Initialize state when initialData changes, but protect against overwriting unsaved work
  useEffect(() => {
    if (!isOpen) return;

    const incomingId = initialData?.id || null;

    // Logic to handle transitioning from "New Note" (null) to "Created Note" (ID)
    // without losing data if the user kept typing during the save.
    const isCreationTransition = lastLoadedIdRef.current === null && incomingId !== null;

    if (incomingId !== lastLoadedIdRef.current) {
      // If we are just linking the created ID to the current session, update the ref and stop.
      // We only do this if there are local changes that might be newer than what the server returned.
      if (isCreationTransition && hasChanges) {
        lastLoadedIdRef.current = incomingId;
        return;
      }

      // Standard Reset: switching notes or opening fresh
      if (initialData) {
        setTitle(initialData.title);
        setContent(initialData.content);
        setColor(initialData.color);
        setIsFavorite(initialData.isFavorite);
      } else {
        // Reset for new note
        setTitle('');
        setContent('');
        setColor('white');
        setIsFavorite(false);
      }
      setHasChanges(false);
      setDeleteConfirmation(false);
      lastLoadedIdRef.current = incomingId;
    }
  }, [initialData, isOpen, hasChanges]);

  // Handle auto-save logic
  useEffect(() => {
    if (!isOpen || !hasChanges) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Auto-save after 1 second of inactivity if there are changes
    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, color, isFavorite, isOpen, hasChanges]);

  // Reset delete confirmation after 3 seconds
  useEffect(() => {
    if (deleteConfirmation) {
      const timer = setTimeout(() => setDeleteConfirmation(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [deleteConfirmation]);

  const handleChange = (setter: React.Dispatch<any>, value: any) => {
    setter(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // SECURITY LOCK: Prevent double-saves (race condition between auto-save and close)
    if (isSavingRef.current) return;
    
    // Don't save if nothing changed on existing note
    if (!hasChanges && initialData) return; 
    
    isSavingRef.current = true;
    setIsSaving(true);
    
    try {
      await onSave({ title, content, color, isFavorite });
      // We assume the save was successful and synced.
      setHasChanges(false); 
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  const handleClose = async () => {
    // If currently saving, just close immediately to avoid hanging the UI
    // The background save will likely finish successfully.
    if (isSavingRef.current) {
        onClose();
        return;
    }

    if (hasChanges) {
      await handleSave(); // Ensure save on close
    }
    onClose();
  };

  const handleDelete = async () => {
    if (!initialData) return;

    if (!deleteConfirmation) {
      setDeleteConfirmation(true);
      return;
    }

    await onDelete(initialData.id);
    onClose();
  };

  const handleRefineWithAI = async () => {
    if (!content.trim() || isRefining) return;

    setIsRefining(true);
    try {
      const refinedContent = await refineText(content);
      if (refinedContent) {
        setContent(refinedContent);
        setHasChanges(true); // Trigger auto-save
      } else {
        console.warn("AI Refinement returned no text. Check API Key.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefining(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Panel */}
      <div className={`
        relative w-full max-w-2xl bg-white shadow-2xl h-full flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Toolbar */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white/80 backdrop-blur-md sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
            >
              <XIcon size={20} />
            </button>
            <span className="text-sm font-medium text-gray-400">
              {isSaving ? 'Saving...' : hasChanges ? 'Unsaved changes' : 'All changes saved'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* AI Button */}
            <button 
              onClick={handleRefineWithAI}
              disabled={isRefining || !content.trim()}
              className={`
                p-2 rounded-full transition-all flex items-center gap-2 mr-2
                ${isRefining 
                  ? 'bg-blue-50 text-blue-500 cursor-wait' 
                  : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}
                ${!content.trim() ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title="Refine with AI"
            >
              <MagicIcon size={20} className={isRefining ? "animate-pulse" : ""} />
            </button>

            <div className="h-6 w-px bg-gray-200 mx-1" />

            <button 
              onClick={() => handleChange(setIsFavorite, !isFavorite)}
              className={`p-2 rounded-full transition-colors ${isFavorite ? 'text-yellow-500 bg-yellow-50' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Toggle Favorite"
            >
              <StarIcon size={20} filled={isFavorite} />
            </button>
            
            <div className="h-6 w-px bg-gray-200 mx-2" />
            
            <div className="flex gap-1">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  onClick={() => handleChange(setColor, c)}
                  className={`
                    w-6 h-6 rounded-full border border-gray-200 shadow-sm
                    ${NOTE_COLORS[c]}
                    ${color === c ? 'ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}
                    transition-all
                  `}
                />
              ))}
            </div>

            {initialData && (
              <>
                <div className="h-6 w-px bg-gray-200 mx-2" />
                <button 
                  onClick={handleDelete}
                  className={`
                    p-2 rounded-full transition-all duration-200 flex items-center gap-2
                    ${deleteConfirmation 
                      ? 'bg-red-500 text-white pr-4 pl-3 hover:bg-red-600' 
                      : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                    }
                  `}
                  title={deleteConfirmation ? "Confirm Delete" : "Delete Note"}
                >
                  <TrashIcon size={20} />
                  {deleteConfirmation && <span className="text-sm font-medium whitespace-nowrap">Confirm</span>}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className={`flex-1 overflow-y-auto p-8 md:p-12 ${NOTE_COLORS[color]} transition-colors duration-500`}>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => handleChange(setTitle, e.target.value)}
            className="w-full bg-transparent text-4xl font-bold text-gray-900 placeholder-gray-400 border-none outline-none mb-6"
          />
          <textarea
            placeholder="Start typing..."
            value={content}
            onChange={(e) => handleChange(setContent, e.target.value)}
            className="w-full h-[calc(100%-80px)] bg-transparent text-lg text-gray-700 placeholder-gray-400 border-none outline-none resize-none leading-relaxed"
            autoFocus={!initialData}
          />
        </div>
      </div>
    </div>
  );
};

export default Editor;